/**
 * WalletSheet — Me / Wallet modal
 *
 * Combines in one view:
 *   • Current balance + recent ledger
 *   • Three recharge packages (Starter / Plus / Premium) — Plus is Most Popular
 *   • Redeem Coupon tile (for users who skipped the welcome popup)
 *   • Refer & Earn tile (reuses /referrals/me)
 *
 * Buy Now intentionally opens a "Recharge Coming Soon" sub-popup — no payment,
 * no mock charge, no credit grant.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Gift, Share2, Copy, Check, Loader2, Sparkles, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/hooks/useAuth";

export default function WalletSheet({ open, onClose }) {
  const referralRef = useRef(null);
  const [focusReferralPending, setFocusReferralPending] = useState(false);

  // Listen for global request to scroll to the Refer & Earn tile
  useEffect(() => {
    const h = () => setFocusReferralPending(true);
    window.addEventListener("kalki:wallet-focus-referral", h);
    return () => window.removeEventListener("kalki:wallet-focus-referral", h);
  }, []);

  // When sheet is open and we have a pending referral-focus request,
  // scroll the Refer & Earn tile into view with a soft highlight.
  useEffect(() => {
    if (!open || !focusReferralPending) return;
    const el = referralRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
      try {
        el.animate(
          [
            { boxShadow: "0 0 0 0 rgba(179,132,255,0)" },
            { boxShadow: "0 0 0 6px rgba(179,132,255,0.35)" },
            { boxShadow: "0 0 0 0 rgba(179,132,255,0)" },
          ],
          { duration: 1600, iterations: 1 }
        );
      } catch (_) {}
      setFocusReferralPending(false);
    }, 400);
    return () => clearTimeout(t);
  }, [open, focusReferralPending]);

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [packages, setPackages] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [referral, setReferral] = useState(null);
  const [comingSoon, setComingSoon] = useState(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, w, r] = await Promise.all([
        api.get("/credits").catch(() => ({ data: {} })),
        api.get("/wallet/packages").catch(() => ({ data: { packages: [] } })),
        api.get("/referrals/me").catch(() => ({ data: null })),
      ]);
      setBalance(c?.data?.balance ?? null);
      setLedger(c?.data?.ledger || []);
      setPackages(w?.data?.packages || []);
      setReferral(r?.data || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  if (!open) return null;

  const redeem = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/codes/apply", { code: coupon.trim() });
      if (data?.type === "coupon") {
        toast.success(`${data.credits} KALKI Credits Added!`);
        if (typeof data.balance === "number") {
          setBalance(data.balance);
          window.dispatchEvent(new CustomEvent("kalki:credits-updated", { detail: { balance: data.balance } }));
        }
      } else if (data?.type === "referral") {
        toast.success(data.title || "Referral Code Applied!", {
          description: data.message,
        });
        // Refresh the referral tile so attribution is reflected.
        refresh();
      } else {
        toast.success("Code Applied");
      }
      setCoupon("");
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      toast.error(d.message || "Invalid code.");
    } finally {
      setCouponBusy(false);
    }
  };

  const buy = async (pkgId) => {
    try {
      const { data } = await api.post("/wallet/buy", { package_id: pkgId });
      setComingSoon(data);
    } catch (e) {
      toast.error("Something went wrong.");
    }
  };

  const copyLink = () => {
    const url = referral?.share_url;
    if (!url) return;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => toast.error("Copy failed"));
  };

  const share = async () => {
    const url = referral?.share_url;
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "KALKI AI",
          text: `Join me on KALKI — begin with 100 welcome + 50 referral credits.`,
          url,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div
      data-testid="wallet-sheet"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 9998,
        background: "rgba(10,7,30,0.72)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={{
        width: "min(560px, 100%)",
        maxHeight: "90vh",
        overflowY: "auto",
        background: "linear-gradient(180deg, #1A0B3F 0%, #0E0527 100%)",
        color: "#F6F1FF",
        borderRadius: 24,
        border: "1px solid rgba(179,132,255,0.22)",
        boxShadow: "0 32px 80px rgba(55,0,170,0.55)",
        padding: "24px 22px 22px",
        fontFamily: "'Inter', sans-serif",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <h2 style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 22, fontWeight: 500,
            letterSpacing: "-0.01em",
            margin: 0,
          }}>Wallet</h2>
          <button
            data-testid="wallet-sheet-close"
            onClick={onClose}
            aria-label="Close wallet"
            style={{
              width: 36, height: 36, borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#D7CBFF", cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          ><X size={16} /></button>
        </div>

        {/* Balance */}
        <div style={{
          padding: "16px 18px",
          borderRadius: 16,
          background: "linear-gradient(135deg, rgba(126,59,255,0.26) 0%, rgba(179,132,255,0.14) 100%)",
          border: "1px solid rgba(179,132,255,0.28)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 16,
        }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "#CFC2FA", textTransform: "uppercase" }}>
              Current Balance
            </div>
            <div data-testid="wallet-balance" style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1, marginTop: 4 }}>
              {balance == null ? "—" : balance.toLocaleString()}
              <span style={{ fontSize: 12, color: "#CFC2FA", marginLeft: 8, fontWeight: 400 }}>credits</span>
            </div>
          </div>
          <Sparkles size={28} color="#B384FF" />
        </div>

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
            <Loader2 size={20} className="animate-spin" style={{ color: "#B384FF" }} />
          </div>
        )}

        {/* Packages */}
        {!loading && packages.length > 0 && (
          <>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "#CFC2FA", textTransform: "uppercase", margin: "6px 0 10px" }}>
              Recharge Packages
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginBottom: 18 }}>
              {packages.map((p) => (
                <div
                  key={p.id}
                  data-testid={`wallet-package-${p.id}`}
                  style={{
                    position: "relative",
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: p.popular ? "linear-gradient(135deg, rgba(126,59,255,0.22) 0%, rgba(179,132,255,0.12) 100%)" : "rgba(255,255,255,0.04)",
                    border: p.popular ? "1px solid rgba(179,132,255,0.5)" : "1px solid rgba(255,255,255,0.08)",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                  }}
                >
                  {p.popular && (
                    <span style={{
                      position: "absolute", top: -9, left: 14,
                      padding: "3px 8px", borderRadius: 999,
                      background: "linear-gradient(135deg,#7E3BFF 0%,#B384FF 100%)",
                      fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                      color: "#FFF", display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      <Crown size={10} /> Most Popular
                    </span>
                  )}
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#CFC2FA", marginTop: 2 }}>{p.benefit}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, color: "#F6F1FF" }}>₹{p.price_inr}</div>
                    <div style={{ fontSize: 11, color: "#B384FF", marginTop: 2 }}>{p.credits} credits</div>
                    <button
                      data-testid={`wallet-buy-${p.id}`}
                      onClick={() => buy(p.id)}
                      style={{
                        marginTop: 6,
                        padding: "7px 12px", borderRadius: 999,
                        background: p.popular ? "linear-gradient(135deg,#7E3BFF 0%,#B384FF 100%)" : "rgba(179,132,255,0.14)",
                        color: "#FFF", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
                        border: "none", cursor: "pointer",
                      }}
                    >Buy Now</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Redeem Coupon or Referral */}
        <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "#CFC2FA", textTransform: "uppercase", margin: "6px 0 10px" }}>
          Redeem Coupon or Referral
        </div>
        <div style={{
          padding: "14px 14px",
          borderRadius: 14,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          display: "flex", gap: 8, alignItems: "center", marginBottom: 18,
        }}>
          <Gift size={18} color="#B384FF" />
          <input
            data-testid="wallet-coupon-input"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value.toUpperCase())}
            placeholder="Enter Coupon or Referral Code"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            style={{
              flex: 1, padding: "10px 12px", borderRadius: 10,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(179,132,255,0.26)",
              color: "#F6F1FF", fontSize: 13, letterSpacing: "0.05em",
              textTransform: "uppercase", outline: "none",
            }}
          />
          <button
            data-testid="wallet-coupon-redeem"
            onClick={redeem}
            disabled={couponBusy || !coupon.trim()}
            style={{
              padding: "10px 14px", borderRadius: 10,
              background: couponBusy || !coupon.trim() ? "rgba(126,59,255,0.4)" : "linear-gradient(135deg,#7E3BFF 0%,#B384FF 100%)",
              color: "#FFF", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em",
              border: "none", cursor: couponBusy ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {couponBusy && <Loader2 size={14} className="animate-spin" />}
            Apply
          </button>
        </div>

        {/* Referral */}
        {referral && (
          <>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", color: "#CFC2FA", textTransform: "uppercase", margin: "6px 0 10px" }}>
              Refer & Earn
            </div>
            <div ref={referralRef} data-testid="wallet-refer-earn-section" style={{
              padding: "14px 14px", borderRadius: 14,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              marginBottom: 10,
            }}>
              <div style={{ fontSize: 12, color: "#CFC2FA" }}>Your code</div>
              <div data-testid="wallet-referral-code" style={{ fontSize: 20, fontWeight: 600, letterSpacing: "0.06em", margin: "4px 0 10px" }}>
                {referral.code}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  data-testid="wallet-referral-copy"
                  onClick={copyLink}
                  style={{
                    flex: 1, padding: "10px 12px", borderRadius: 10,
                    background: "rgba(179,132,255,0.14)",
                    color: "#F6F1FF", fontSize: 12, fontWeight: 500,
                    border: "1px solid rgba(179,132,255,0.26)",
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied!" : "Copy Link"}
                </button>
                <button
                  data-testid="wallet-referral-share"
                  onClick={share}
                  style={{
                    flex: 1, padding: "10px 12px", borderRadius: 10,
                    background: "linear-gradient(135deg,#7E3BFF 0%,#B384FF 100%)",
                    color: "#FFF", fontSize: 12, fontWeight: 600,
                    border: "none", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                >
                  <Share2 size={14} /> Share
                </button>
              </div>
              <div style={{ display: "flex", gap: 10, fontSize: 11, color: "#CFC2FA", marginTop: 12, flexWrap: "wrap" }}>
                <span>Earned: <strong style={{ color: "#F6F1FF" }}>{referral.earned_credits ?? 0}</strong></span>
                <span>·</span>
                <span>Rewarded this month: <strong style={{ color: "#F6F1FF" }}>{referral.rewarded_this_month ?? 0} / {referral.monthly_cap ?? 20}</strong></span>
              </div>
            </div>
          </>
        )}

        {/* P0-4 (Build 6): Credit History — every charge, grant, refund.
            Reads the same /api/credits payload as the balance so pill +
            Wallet + history are always in sync. */}
        <div style={{ marginTop: 18, fontSize: 11, letterSpacing: "0.14em", color: "#CFC2FA", textTransform: "uppercase" }}>
          Credit History
        </div>
        <div data-testid="wallet-history"
             style={{ marginTop: 10, borderRadius: 14,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      maxHeight: 240, overflowY: "auto" }}>
          {ledger.length === 0 ? (
            <div style={{ padding: "14px 16px", color: "#9D8EC9", fontSize: 13 }}>
              Your KALKI Credit activity will show up here.
            </div>
          ) : (
            ledger.map((row, i) => {
              const delta = Number(row.delta || row.amount || 0);
              const isDebit = delta < 0;
              const title =
                row.description
                || ({
                      "life_report": "Life Report",
                      "question": "Chat question",
                      "voice": "Voice call",
                      "sankalp_create": "Took a Sankalp",
                      "prasad_offer": "Prasad offered",
                      "welcome_bonus": "Welcome bonus",
                      "coupon_redeem": "Coupon redeemed",
                      "referral_bonus": "Referral bonus",
                      "kalki_special_1000_v2": "KALKI special grant",
                      "report_refund": "Report refund",
                    }[row.action] || row.action || "Credit activity");
              const when = (row.created_at || "").slice(0, 16).replace("T", " ");
              return (
                <div key={row.id || i}
                     data-testid={`wallet-history-row-${i}`}
                     style={{
                       display: "flex", justifyContent: "space-between",
                       alignItems: "center", gap: 10,
                       padding: "11px 14px",
                       borderBottom: i === ledger.length - 1 ? "none" : "1px solid rgba(255,255,255,0.05)",
                     }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, color: "#F6F1FF", textTransform: "capitalize" }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 11, color: "#8E7FB8", marginTop: 2 }}>
                      {when}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.02em",
                                color: isDebit ? "#FF9F7A" : "#8CCF04" }}>
                    {isDebit ? "" : "+"}{delta}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Coming Soon sub-popup */}
        {comingSoon && (
          <div
            data-testid="wallet-coming-soon"
            onClick={() => setComingSoon(null)}
            style={{
              position: "fixed", inset: 0, zIndex: 10000,
              background: "rgba(10,7,30,0.78)", backdropFilter: "blur(10px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16,
            }}>
            <div style={{
              width: "min(380px, 100%)",
              background: "linear-gradient(180deg,#1A0B3F 0%,#0E0527 100%)",
              borderRadius: 20,
              border: "1px solid rgba(179,132,255,0.22)",
              padding: "26px 20px 20px",
              textAlign: "center",
              color: "#F6F1FF",
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 999,
                margin: "0 auto 14px",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg,#7E3BFF,#B384FF)",
                boxShadow: "0 8px 32px rgba(93,0,255,0.5)",
              }}>
                <Sparkles size={28} color="#FFF" />
              </div>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 500, margin: "0 0 6px" }}>
                {comingSoon.title || "Recharge Coming Soon"}
              </h3>
              <p style={{ fontSize: 13, color: "#CFC2FA", margin: "0 0 16px" }}>
                {comingSoon.message || "Secure KALKI Credit purchases are on their way."}
              </p>
              <button
                onClick={() => setComingSoon(null)}
                style={{
                  padding: "11px 22px", borderRadius: 10,
                  background: "linear-gradient(135deg,#7E3BFF,#B384FF)",
                  color: "#FFF", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em",
                  border: "none", cursor: "pointer",
                }}
              >Got it</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
